// Admin Dashboard with Analytics
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { OrderService } from '../../services/orderService';
import { AnalyticsService } from '../../services/analyticsService';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  BarChart3,
  DollarSign,
  ShoppingBag,
  Users,
  TrendingUp,
  Package,
  AlertCircle,
  Truck,
  Calculator
} from 'lucide-react';

export default function AdminDashboard() {
  const { isAdmin, currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdmin) {
      loadDashboardData();
    }
  }, [isAdmin]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [orderStats, analyticsData] = await Promise.all([
        OrderService.getOrderStats(),
        AnalyticsService.getAnalyticsData()
      ]);
      setStats(orderStats);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center">
        <Card className="max-w-md text-center">
          <CardContent className="pt-12 pb-12">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              You don't have permission to access this page.
            </p>
            <Button onClick={() => window.location.hash = '#/'}>
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-5xl font-bold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Admin Dashboard
          </h1>
          <p className="text-xl text-muted-foreground">
            Welcome back, {currentUser?.displayName || 'Admin'}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Revenue */}
          <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="h-8 w-8 text-primary" />
                <TrendingUp className="h-4 w-4 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">Total Revenue</p>
              <p className="text-3xl font-bold text-foreground">
                ${stats?.totalRevenue?.toFixed(2) || '0.00'}
              </p>
            </CardContent>
          </Card>

          {/* Total Orders */}
          <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <ShoppingBag className="h-8 w-8 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">Total Orders</p>
              <p className="text-3xl font-bold text-foreground">
                {stats?.total || 0}
              </p>
            </CardContent>
          </Card>

          {/* Pending Orders */}
          <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <Package className="h-8 w-8 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">Pending Orders</p>
              <p className="text-3xl font-bold text-foreground">
                {stats?.pending || 0}
              </p>
            </CardContent>
          </Card>

          {/* Total Events */}
          <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <BarChart3 className="h-8 w-8 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">Total Events</p>
              <p className="text-3xl font-bold text-foreground">
                {analytics?.totalEvents || 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Management Tools */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Truck className="h-8 w-8 text-primary" />
                <div>
                  <h3 className="text-xl font-bold">Supplier Management</h3>
                  <p className="text-sm text-muted-foreground">
                    View and manage all suppliers
                  </p>
                </div>
              </div>
              <Button
                onClick={() => window.location.hash = '#/admin/suppliers'}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Manage Suppliers
              </Button>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Calculator className="h-8 w-8 text-primary" />
                <div>
                  <h3 className="text-xl font-bold">Product Costs</h3>
                  <p className="text-sm text-muted-foreground">
                    Analyze pricing and margins
                  </p>
                </div>
              </div>
              <Button
                onClick={() => window.location.hash = '#/admin/product-costs'}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                View Product Costs
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Orders */}
        <Card className="backdrop-blur-sm bg-card/70 border-2 border-border mb-8">
          <CardHeader>
            <CardTitle>Recent Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.recentOrders?.slice(0, 5).map(order => (
                <div
                  key={order.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/40"
                >
                  <div>
                    <p className="font-semibold">Order #{order.id.slice(0, 8)}</p>
                    <p className="text-sm text-muted-foreground">
                      {order.userEmail}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">
                      ${order.total?.toFixed(2)}
                    </p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      order.status === 'completed' ? 'bg-secondary text-secondary-foreground' :
                      order.status === 'processing' ? 'bg-muted text-foreground' :
                      order.status === 'pending' ? 'bg-accent text-foreground' :
                      'bg-destructive/20 text-destructive'
                    }`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Analytics Overview */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Event Types */}
            <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
              <CardHeader>
                <CardTitle>Event Types</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(analytics.eventsByType || {}).map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground capitalize">
                        {type.replace('_', ' ')}
                      </span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Searches */}
            <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
              <CardHeader>
                <CardTitle>Top Searches</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(analytics.topSearches || {})
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 5)
                    .map(([query, count]) => (
                      <div key={query} className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          "{query}"
                        </span>
                        <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
